import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getEventLogsActions, getEventLogsResources } from "../../services";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import dayjs from "dayjs";
import "dayjs/locale/th";

dayjs.locale("th");

interface ReportFilterProps {
  startDate: Dayjs | null;
  setStartDate: Dispatch<SetStateAction<Dayjs | null>>;
  endDate: Dayjs | null;
  setEndDate: Dispatch<SetStateAction<Dayjs | null>>;
  selectedAction: string;
  setSelectedAction: Dispatch<SetStateAction<string>>;
  selectedResource: string;
  setSelectedResource: Dispatch<SetStateAction<string>>;
}

interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
  size?: { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
}

const FilterField = ({
  label,
  children,
  size = { xs: 12, sm: 6 },
}: FilterFieldProps) => (
  <Grid size={size}>
    <Grid container alignItems="center" spacing={1}>
      <Grid size={2}>
        <Typography variant="body2" noWrap>
          {label}
        </Typography>
      </Grid>
      <Grid size={10}>{children}</Grid>
    </Grid>
  </Grid>
);

export default function EventLogsFilter({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedAction,
  setSelectedAction,
  selectedResource,
  setSelectedResource,
}: ReportFilterProps) {
  const [actions, setActions] = useState<string[]>([]);
  const [resources, setResources] = useState<string[]>([]);

  const onGetActions = useCallback(async () => {
    const {
      data: { data },
    } = await getEventLogsActions();
    setActions(data);
  }, []);

  const onGetResources = useCallback(async () => {
    const {
      data: { data },
    } = await getEventLogsResources();
    setResources(data);
  }, []);

  useEffect(() => {
    onGetActions();
    onGetResources();
  }, [onGetActions, onGetResources]);

  return (
    <Grid container spacing={2} mb={2}>
      <FilterField label="ประเภทข้อมูล">
        <TextField
          select
          size="small"
          fullWidth
          value={selectedResource}
          onChange={(e) => setSelectedResource(e.target.value)}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>
          {resources.map((resource) => (
            <MenuItem key={resource} value={resource}>
              {resource}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="การดำเนินการ">
        <TextField
          select
          size="small"
          fullWidth
          value={selectedAction}
          onChange={(e) => setSelectedAction(e.target.value)}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>
          {actions.map((action) => (
            <MenuItem key={action} value={action}>
              {action}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="วันที่" size={{ xs: 12, lg: 12, xl: 6 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
          <Box sx={{ display: "flex", gap: 1 }}>
            <DatePicker
              label="เริ่มต้น"
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
              value={startDate}
              onChange={(newValue) => newValue && setStartDate(newValue)}
            />
            {startDate && (
              <IconButton
                size="small"
                sx={{ ml: 0.5 }}
                onClick={() => setStartDate(null)}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
            <DatePicker
              label="สิ้นสุด"
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
              value={endDate}
              onChange={(newValue) => newValue && setEndDate(newValue)}
            />
            {endDate && (
              <IconButton
                size="small"
                sx={{ ml: 0.5 }}
                onClick={() => setEndDate(null)}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </LocalizationProvider>
      </FilterField>
    </Grid>
  );
}
